import { useEffect, useState } from 'react';
import { buildIdpHandoffUrl, fetchOidcHandoffConfig, type OidcHandoffConfig } from '@/api';

// Per-apiRoot module cache — handoff config doesn't change without a backend
// restart; one fetch per tab is plenty.
const configCache = new Map<string, Promise<OidcHandoffConfig | null>>();

function getConfig(apiRoot: string): Promise<OidcHandoffConfig | null> {
  if (!configCache.has(apiRoot)) {
    configCache.set(apiRoot, fetchOidcHandoffConfig(apiRoot));
  }
  return configCache.get(apiRoot)!;
}

export interface UseIdpHandoffResult {
  config: OidcHandoffConfig | null;
  loading: boolean;
  /** Returns null when the action verb isn't in the portal's supported list. */
  buildUrl: (action: string, returnTo: string) => string | null;
  accountManagementUri: string | null;
}

/**
 * Pass the backend root (without `/v1`). 404 → null config → SPA hides SSO
 * deep-links.
 */
export function useIdpHandoff(apiRoot: string): UseIdpHandoffResult {
  const [config, setConfig] = useState<OidcHandoffConfig | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getConfig(apiRoot)
      .then((cfg) => {
        if (!cancelled) {
          setConfig(cfg);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [apiRoot]);

  return {
    config,
    loading,
    buildUrl: (action, returnTo) => {
      if (!config) return null;
      return buildIdpHandoffUrl({ config, action, returnTo });
    },
    accountManagementUri: config?.account_management_uri ?? null,
  };
}
